import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { GLOBAL_STATE_TOKEN } from '~backoffice/app/~shared/constants/global.constants';
import { GlobalStateModel } from '~backoffice/app/~shared/models/store.models';
import {
  ClearCurrentResource,
  SetCurrentResource,
  SetFormViolations,
  SetOnlineStatus,
  SetRouteData
} from '~backoffice/app/~store/actions/global.actions';
import { toPath } from 'lodash-es';
import { Resource } from '~core/models/shared.models';

export const DEFAULT_GLOBAL_STATE = {
  routeData: undefined,
  formViolations: undefined,
  isDeviceOnline: undefined,
  isDevicePreviouslyOnline: undefined,
  currentResource: undefined
};

@State({
  name: GLOBAL_STATE_TOKEN,
  defaults: DEFAULT_GLOBAL_STATE
})
@Injectable()
export class GlobalState {
  @Action(SetRouteData)
  setRouteData(
    ctx: StateContext<GlobalStateModel>,
    action: SetRouteData
  ): void {
    const { routeData, formViolations } = ctx.getState();

    ctx.patchState({
      routeData: {
        ...routeData,
        ...action.payload
      }
    });

    if (formViolations?.length) {
      ctx.dispatch(new SetFormViolations(undefined));
    }
  }

  @Action(SetFormViolations)
  setFormViolations(
    ctx: StateContext<GlobalStateModel>,
    action: SetFormViolations
  ): GlobalStateModel | void {
    const violations = action.payload;
    return ctx.patchState({
      formViolations:
        !!violations && violations.length
          ? violations.map(el => {
              if (el.propertyPath) {
                switch (el.propertyPath) {
                  case 'plainPassword':
                    el.propertyPath = ['password'];
                    break;

                  default:
                    el.propertyPath = toPath(el.propertyPath);
                    break;
                }
              }

              return el;
            })
          : DEFAULT_GLOBAL_STATE.formViolations
    });
  }

  @Action(SetOnlineStatus)
  setOnlineStatus(
    ctx: StateContext<GlobalStateModel>,
    action: SetOnlineStatus
  ): void {
    const { isDeviceOnline } = ctx.getState();
    ctx.patchState({
      isDeviceOnline: action.payload,
      isDevicePreviouslyOnline: isDeviceOnline
    });

    const isBackOnline = isDeviceOnline === false && action.payload === true;
    if (isBackOnline) {
      console.log('device is back online');
      return;
    }

    if (action.payload === false) {
      console.log('you are offline.');
    }
  }

  @Action(SetCurrentResource)
  setCurrentResource(
    ctx: StateContext<GlobalStateModel>,
    action: SetCurrentResource
  ): void {
    ctx.patchState({
      currentResource: action.payload
    });
  }

  @Action(ClearCurrentResource)
  clearCurrentResource(ctx: StateContext<GlobalStateModel>): void {
    ctx.patchState({
      currentResource: undefined
    });
  }

  // SELECTORS

  @Selector()
  static routeData(
    state: GlobalStateModel
  ): Pick<GlobalStateModel, 'routeData'>['routeData'] {
    return state.routeData;
  }

  @Selector()
  static params(state: GlobalStateModel): Record<string, string> | undefined {
    return state.routeData?.params;
  }

  @Selector()
  static queryParams(
    state: GlobalStateModel
  ): Record<string, string> | undefined {
    return state.routeData?.queryParams;
  }

  @Selector()
  static currentResource(state: GlobalStateModel): Resource | undefined {
    return state.currentResource;
  }

  @Selector()
  static formViolations(
    state: GlobalStateModel
  ): Pick<GlobalStateModel, 'formViolations'>['formViolations'] {
    return state.formViolations;
  }

  @Selector()
  static isDeviceOnline(
    state: GlobalStateModel
  ): Pick<GlobalStateModel, 'isDeviceOnline'>['isDeviceOnline'] {
    return state.isDeviceOnline;
  }
}
